import {
  Identifiable,
  Project,
  ProjectReport,
  Zone,
  zoneProjectsRetrievalFilter,
} from "@planzone/dto";
import { Repository } from "../../adapters/repository";
import { present } from "../../../utils/presenter";
import { mapWithConcurrency } from "../../../utils/concurrency";
import { Interactor } from "./types";

const ZONE_PROJECTS_CONCURRENCY = 6;
const PROJECT_REPORTS_CONCURRENCY = 10;

function flattenProjects(
  projectsChunks: {
    project: Identifiable<Project>;
    zone: Identifiable<Zone>;
  }[][],
): { project: Identifiable<Project>; zone: Identifiable<Zone> }[] {
  return projectsChunks.reduce(
    (projects, chunk) => [...projects, ...chunk],
    [],
  );
}

function flattenReports(
  reportsChunk: {
    project: Identifiable<Project>;
    zone: Identifiable<Zone>;
    report: Identifiable<ProjectReport>;
  }[][],
): {
  project: Identifiable<Project>;
  zone: Identifiable<Zone>;
  report: Identifiable<ProjectReport>;
}[] {
  return reportsChunk.reduce((reports, chunk) => [...reports, ...chunk], []);
}

function createInteractor(repository: Repository): Interactor {
  return (planzone) =>
    present(
      repository
        .getZones(planzone)
        .then((zones) =>
          mapWithConcurrency(zones, ZONE_PROJECTS_CONCURRENCY, (zone) =>
            repository
              .getZoneProjects(zone, {
                ...zoneProjectsRetrievalFilter,
                limit: 99999999,
              })
              .then((projects) =>
                projects.map((project) => ({
                  project,
                  zone,
                })),
              ),
          ),
        )
        .then(flattenProjects)
        .then((res) =>
          mapWithConcurrency(
            res,
            PROJECT_REPORTS_CONCURRENCY,
            ({ zone, project }) =>
              repository
                .getProjectReports(project)
                .then((reports) =>
                  reports.map((report) => ({ zone, project, report })),
                )
                .catch(() => []),
          ),
        )
        .then(flattenReports)
        .then((reports) => ({ reports, planzone }))
        .catch((error) => Promise.reject(planzone)),
    );
}

export type { Interactor };

export { createInteractor };
